import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Star, X, Upload, User } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    testimonial_text: '',
    project_name: '',
    rating: 5,
    is_active: true,
    customer_photo: '',
  });

  useEffect(() => {
    fetchTestimonials();
    fetchProjects();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/testimonials/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setFormData({
      customer_name: '',
      testimonial_text: '',
      project_name: '',
      rating: 5,
      is_active: true,
      customer_photo: '',
    });
    setShowModal(true);
  };

  const openEditModal = (testimonial) => {
    setEditingId(testimonial.id);
    setFormData({
      customer_name: testimonial.customer_name,
      testimonial_text: testimonial.testimonial_text,
      project_name: testimonial.project_name,
      rating: testimonial.rating,
      is_active: testimonial.is_active,
      customer_photo: testimonial.customer_photo || '',
    });
    setShowModal(true);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    const token = localStorage.getItem('admin_token');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setFormData({ ...formData, customer_photo: response.data.url });
      toast.success('Photo uploaded successfully');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      if (editingId) {
        await axios.put(`${API}/testimonials/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Testimonial updated successfully');
      } else {
        await axios.post(`${API}/testimonials`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Testimonial added successfully');
      }
      setShowModal(false);
      fetchTestimonials();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast.error('Failed to save testimonial');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast.error('Failed to delete testimonial');
    }
  };

  const toggleActive = async (testimonial) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(`${API}/testimonials/${testimonial.id}`, 
        { is_active: !testimonial.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTestimonials();
      toast.success(`Testimonial ${!testimonial.is_active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update testimonial');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/admin/dashboard" className="flex items-center gap-3">
              <span className="text-xl font-bold text-gray-900">Infinity Group</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard" className="text-gray-600 hover:text-gray-900">Dashboard</Link>
              <Link to="/admin/projects" className="text-gray-600 hover:text-gray-900">Projects</Link>
              <Link to="/admin/testimonials" className="text-blue-600 font-medium">Testimonials</Link>
              <Link to="/admin/settings" className="text-gray-600 hover:text-gray-900">Settings</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Testimonials</h1>
          <Button onClick={openAddModal} className="bg-blue-600 hover:bg-blue-700">
            <Plus size={18} className="mr-2" />
            Add Testimonial
          </Button>
        </div>

        {testimonials.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm">
            <p className="text-gray-500 mb-4">No testimonials yet</p>
            <Button onClick={openAddModal} className="bg-blue-600 hover:bg-blue-700">
              Add Your First Testimonial
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.id} 
                className={`bg-white rounded-xl p-6 shadow-sm ${!testimonial.is_active ? 'opacity-60' : ''}`}
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="flex gap-4 flex-1">
                    {testimonial.customer_photo && (
                      <img 
                        src={testimonial.customer_photo} 
                        alt={testimonial.customer_name}
                        className="w-32 h-24 rounded-lg object-cover flex-shrink-0"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900">{testimonial.customer_name}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${testimonial.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {testimonial.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            size={16} 
                            className={i < testimonial.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} 
                          />
                        ))}
                      </div>
                      <p className="text-gray-600 mb-2 italic">"{testimonial.testimonial_text}"</p>
                      <p className="text-sm text-gray-500">Project: {testimonial.project_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => toggleActive(testimonial)}
                      className={`px-3 py-1 text-sm rounded ${testimonial.is_active ? 'bg-gray-100 hover:bg-gray-200' : 'bg-green-100 hover:bg-green-200'}`}
                    >
                      {testimonial.is_active ? 'Hide' : 'Show'}
                    </button>
                    <button
                      onClick={() => openEditModal(testimonial)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(testimonial.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {editingId ? 'Edit Testimonial' : 'Add Testimonial'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                <input
                  type="text"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
                <select
                  value={formData.project_name}
                  onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a project</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.name}>{project.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rating *</label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: num })}
                      className="p-1"
                    >
                      <Star 
                        size={28} 
                        className={num <= formData.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} 
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Testimonial Text *</label>
                <textarea
                  value={formData.testimonial_text}
                  onChange={(e) => setFormData({ ...formData, testimonial_text: e.target.value })}
                  required
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Share your experience..."
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key Handover / Ceremony Photo (Optional)</label>
                <div className="space-y-3">
                  {formData.customer_photo ? (
                    <div className="relative inline-block">
                      <img 
                        src={formData.customer_photo} 
                        alt="Handover Photo" 
                        className="w-full max-w-md h-48 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, customer_photo: '' })}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
                        <Upload size={32} className="mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600">
                          {uploadingPhoto ? 'Uploading...' : 'Click to upload photo'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">Key handover, ceremony, or customer photo</p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        disabled={uploadingPhoto}
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-blue-600"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">Show on website</label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                  {editingId ? 'Update' : 'Add'} Testimonial
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
